import './Login.css';
import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import {
    requestOTP,
    verifyOTP,
    createUserGoogle,
    setToken as setCoreToken
} from '@genfun/core/api/auth';
import {
    RedButton,
    RedInput,
    GoogleButton,
    OutlineButton,
    DiscordButton,
    BackArrow
} from '../components/common';
import SupportEmail from '../components/SupportEmail';
import LegalNotice from '../components/LegalNotice';

// Shared function for requesting OTP code
const handleResendCode = async (email, setError, setResending = null, setAwaitingCode = null, ref = null) => {
    if (setResending) setResending(true);
    try {
        const [hasError, status] = await requestOTP(email, ref);
        if (hasError) {
            if (status === 404) {
                setError("User not found");
            } else if (status === 400) {
                setError("User is registered through Google");
            } else {
                setError("Failed to send OTP. Please try again.");
            }
            return false;
        } else {
            if (setAwaitingCode) {
                setError(null);
                setAwaitingCode(true);
            } else {
                setError("Code resent successfully!");
                setTimeout(() => setError(null), 3000);
            }
            return true;
        }
    } catch (err) {
        setError("An error occurred. Please try again.");
        console.error(err);
        return false;
    } finally {
        if (setResending) setResending(false);
    }
};

async function googleAction(creds, setError, setToken, navigate, ref = null) {
    const resp = await createUserGoogle(creds, ref);
    setCoreToken(resp.access_token);
    if (resp.access_token === undefined && resp.detail !== undefined) {
        setError(resp.detail);
    } else {
        if (setToken) setToken(resp.access_token);
        navigate('/video');
    }
}

function EmailStage({ emailRef, setAwaitingCode, setError, error, loading, setLoading, setToken, navigate }) {
    const ref = localStorage.getItem('referral_code');

    const login = useGoogleLogin({
        onSuccess: tokenResponse => googleAction(tokenResponse, setError, setToken, navigate, ref),
        onError: () => setError("Google login failed. Please try again.")
    });

    const handleEmailSubmit = async () => {
        const email = emailRef.current?.value;
        if (!email || email.trim() === "") {
            setError("Please enter your email");
            return;
        }

        setLoading(true);
        await handleResendCode(email, setError, null, setAwaitingCode, ref);
        setLoading(false);
    };

    return (
        <>
            <div className="loginCards">
                <div className="loginCard">
                    <RedInput
                        inputRef={emailRef}
                        label="Email"
                        placeholder="user@example.com"
                        type="email"
                    />
                    <RedButton onClick={handleEmailSubmit} disabled={loading}>
                        {loading ? "Sending..." : "Continue with email"}
                    </RedButton>
                </div>
                <div className="loginCard loginCardGoogle">
                    <GoogleButton onClick={() => login()} />
                </div>
            </div>
            {error && <div className="loginError">{error}</div>}
        </>
    );
}

function OTPStage({ email, setAwaitingCode, setError, error, setToken, navigate }) {
    const otpRef = useRef(null);
    const [resending, setResending] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleVerify = async () => {
        const code = otpRef.current?.value;
        if (!code || code.trim() === "") {
            setError("Please enter the verification code");
            return;
        }

        setLoading(true);
        try {
            const [response, status] = await verifyOTP(email, code);

            if (status === 200) {
                if (response && response.access_token) {
                    setCoreToken(response.access_token);
                    if (setToken) setToken(response.access_token);
                }
                navigate('/video');
            } else {
                if (response && response.detail) {
                    setError(response.detail);
                } else {
                    setError("Invalid verification code. Please try again.");
                }
            }
        } catch (err) {
            setError("An error occurred. Please try again.");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="loginCards">
            <div className="loginCard">
                <div className="otpHeader">
                    <BackArrow onClick={() => {
                        setError(null);
                        setAwaitingCode(false);
                    }} />
                    <div className="otpInfo">
                        We've sent you a code to: <span className="otpEmail">{email}</span>
                    </div>
                    <div className="otpInfo">Please check your inbox and spam folder</div>
                </div>
                <RedInput
                    inputRef={otpRef}
                    label="Code from email"
                    placeholder="Enter code"
                    type="text"
                />
                <span
                    className="resendLink"
                    onClick={() => handleResendCode(email, setError, setResending)}
                >
                    {resending ? "Sending..." : "Resend code"}
                </span>
                {error && <div className="loginError">{error}</div>}
                <RedButton onClick={handleVerify} disabled={loading}>
                    {loading ? "Verifying..." : "Log in"}
                </RedButton>
            </div>
        </div>
    );
}

function Footer() {
    return (
        <footer className="loginFooter">
            <SupportEmail />
            <div className="footerButtons">
                <div className="policyButtons">
                    <OutlineButton onClick={() => window.open('/terms', '_blank')}>
                        Terms of use
                    </OutlineButton>
                    <OutlineButton onClick={() => window.open('/privacy', '_blank')}>
                        Privacy policy
                    </OutlineButton>
                </div>
                {/*<div className="discordButtonWrapper">*/}
                {/*    <DiscordButton onClick={() => window.open('https://discord.gg/your-server', '_blank')} />*/}
                {/*</div>*/}
            </div>
            <LegalNotice />
        </footer>
    );
}

export default function Login({ setToken, setUser, setSignedIn }) {
    const navigate = useNavigate();
    const emailRef = useRef(null);
    const [awaitingCode, setAwaitingCode] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    return (
        <div className="loginPage">
            <div className="loginContent">
                <h1 className="loginTitle">Log in or create an account</h1>
                {!awaitingCode ? (
                    <EmailStage
                        emailRef={emailRef}
                        setAwaitingCode={setAwaitingCode}
                        setError={setError}
                        error={error}
                        loading={loading}
                        setLoading={setLoading}
                        setToken={setToken}
                        navigate={navigate}
                    />
                ) : (
                    <OTPStage
                        email={emailRef.current?.value}
                        setAwaitingCode={setAwaitingCode}
                        setError={setError}
                        error={error}
                        setToken={setToken}
                        navigate={navigate}
                    />
                )}
            </div>
            <Footer />
        </div>
    );
}
